window.mcb = window.mcb || {};
window.mcb.utilities = window.mcb.utilities || {};

// Log target implementation
window.mcb.utilities.logTarget = (function () {
    const _logTarget = document.createElement('ul');
    _logTarget.id = 'mcb-console';
    const MAX_LOG_ENTRIES = 1000;

    const pruneOldEntries = function () {
        while (_logTarget.children.length > MAX_LOG_ENTRIES) {
            _logTarget.removeChild(_logTarget.firstChild);
        }
    };

    return {
        render: function () {
            try {
                if (document.body) {
                    document.body.appendChild(_logTarget);
                    window.mcb.utilities.debug.render();
                } else {
                    document.addEventListener('DOMContentLoaded', () => {
                        document.body.appendChild(_logTarget);
                        window.mcb.utilities.debug.render();
                    });
                }
            } catch (e) {
                console.error('Failed to render log target:', e);
            }
        },
        appendChild: function (element) {
            const autoScrollPosition = _logTarget.scrollTop === 0 || _logTarget.scrollTop !== _logTarget.scrollHeight;
            _logTarget.appendChild(element);
            pruneOldEntries();
            if (autoScrollPosition) {
                _logTarget.scrollTop = _logTarget.scrollHeight;
            }
        },
        clear: function () {
            while (_logTarget.firstChild) {
                _logTarget.removeChild(_logTarget.firstChild);
            }
        },
        exportLogs: function () {
            return Array.from(_logTarget.children).map(node => ({
                time: node.querySelector('.mcb-debug-stamp').textContent,
                message: node.querySelector('.mcb-debug-message').textContent,
                namespace: node.querySelector('.mcb-debug-namespace').textContent,
                type: node.className.match(/mcb-debug-(log|warn|error|trace)/)?.[1] || 'log'
            }));
        }
    }
})();

// Logger implementation
window.mcb.utilities.logger = (function () {
    const Logger = function (namespace, prettyName = null, addLoadMessage = true) {
        // Dependency check
        if (!window.mcb?.utilities?.debug?.isDebuggingArea) {
            console.error('Logger dependencies not initialized. Ensure window.mcb.utilities.debug is available');
            return {
                log: console.log.bind(console),
                warn: console.warn.bind(console),
                error: console.error.bind(console),
                trace: console.log.bind(console)
            };
        }

        const _namespace = namespace || 'mcb';
        let _prettyName = prettyName;
        const _active = window.mcb.utilities.debug.isDebuggingArea(_namespace);
        const _verbose = _active && window.mcb.utilities.debug.isVerbose();

        // Private utility functions
        const escapeHtml = function (unsafe) {
            return unsafe
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#039;");
        };

        const isConsoleAvailable = function () {
            return typeof console !== 'undefined' && typeof console.log !== 'undefined';
        };

        // Message buffering for performance
        const messageBuffer = [];
        let flushTimeout = null;
        const FLUSH_INTERVAL = 100; // ms

        const flushMessages = function () {
            if (messageBuffer.length === 0) return;

            try {
                const fragment = document.createDocumentFragment();
                messageBuffer.forEach(msg => fragment.appendChild(msg));
                window.mcb.utilities.logTarget.appendChild(fragment);
            } catch (e) {
                console.error('Failed to flush log messages:', e);
            }
            messageBuffer.length = 0;
            flushTimeout = null;
        };

        const scheduleFlush = function () {
            if (!flushTimeout) {
                flushTimeout = setTimeout(flushMessages, FLUSH_INTERVAL);
            }
        };

        // Core logging functionality
        const writeToDebugTarget = function (args, cssclass) {
            try {
                const newItem = document.createElement('li');
                newItem.className = cssclass;
                newItem.classList.add('mcb-debug-item');

                const newStamp = document.createElement('pre');
                newStamp.className = 'mcb-debug-stamp';
                newStamp.classList.add('mcb-debug--pre');
                const date = new Date();
                const hrs = "0" + date.getHours();
                const mns = "0" + date.getMinutes();
                const sec = "0" + date.getSeconds();
                const ms = "00" + date.getMilliseconds();
                newStamp.textContent = `${hrs.slice(-2)}:${mns.slice(-2)}:${sec.slice(-2)}.${ms.slice(-3)}`;
                newItem.appendChild(newStamp);

                let text = null;
                const blocks = [];

                for (let index = 0; index < args.length; index++) {
                    if (index === 0 && typeof args[index] === "string") {
                        text = args[index].trim();
                        continue;
                    }

                    try {
                        blocks.push('<code>' + escapeHtml(JSON.stringify(args[index], null, 2)) + '</code>');
                    } catch (e) {
                        blocks.push('<code>Error stringifying argument: ' + escapeHtml(String(e)) + '</code>');
                    }
                }

                let newMessage;
                const blocked = blocks.join(', ');
                if (blocked.length > 75) {
                    newMessage = document.createElement('details');
                    newMessage.classList.add('mcb-debug--detailed');
                    const summary = text === null ? `[${blocked.length}]...` : text;

                    const summaryElement = document.createElement('summary');
                    summaryElement.innerHTML = summary;
                    newMessage.appendChild(summaryElement);

                    const detailsElement = document.createElement('pre');
                    detailsElement.classList.add('mcb-debug--pre');
                    detailsElement.innerHTML = blocked;
                    newMessage.appendChild(detailsElement);
                } else {
                    newMessage = document.createElement('pre');
                    newMessage.classList.add('mcb-debug--pre');
                    if (text !== null) {
                        if (blocked.length > 0 && !text.endsWith(':')) {
                            text += ', ';
                        }
                        newMessage.innerHTML = escapeHtml(text) + ' ' + blocked;
                    } else {
                        newMessage.innerHTML = blocked;
                    }
                }

                newMessage.classList.add('mcb-debug-message');
                newMessage.classList.add(text === null ? 'mcb-debug--data-only' : 'mcb-debug--texted');

                newItem.appendChild(newMessage);

                const namespacer = document.createElement('pre');
                namespacer.className = 'mcb-debug-namespace';
                namespacer.textContent = _namespace;
                newItem.appendChild(namespacer);

                messageBuffer.push(newItem);
                scheduleFlush();
            } catch (e) {
                console.error('Failed to create log message:', e);
            }
        };

        // Public logging methods
        const log = function () {
            if (_active) {
                try {
                    if (isConsoleAvailable()) {
                        console.log(...arguments);
                    }
                    writeToDebugTarget(arguments, "mcb-debug-log");
                } catch (e) {
                    console.error('Failed to log message:', e);
                }
            }
        };

        const warn = function () {
            try {
                if (isConsoleAvailable()) {
                    console.warn(...arguments);
                }
                if (_active) {
                    writeToDebugTarget(arguments, 'mcb-debug-warn');
                }
            } catch (e) {
                console.error('Failed to log warning:', e);
            }
        };

        const error = function () {
            try {
                if (isConsoleAvailable()) {
                    console.error(...arguments);
                }
                if (_active) {
                    writeToDebugTarget(arguments, 'mcb-debug-error');
                }
            } catch (e) {
                console.error('Failed to log error:', e);
            }
        };

        const trace = function () {
            if (_active && _verbose) {
                try {
                    if (isConsoleAvailable()) {
                        console.log(...arguments);
                    }
                    writeToDebugTarget(arguments, "mcb-debug-trace");
                } catch (e) {
                    console.error('Failed to log trace:', e);
                }
            }
        };

        // Initialize if requested
        if (addLoadMessage && _active) {
            log(`Logger initialized for ${_prettyName || _namespace}`);
        }

        // Return public interface
        return {
            log,
            warn,
            error,
            trace
        };
    };

    // Return constructor
    return Logger;
})();
